import { parseString } from 'xml2js';

/**
 * Parses a string and returns the appropriate type based on its content
 * @param input - The input string that can be JSON, XML, or plain text
 * @returns - string (if plain text), parsed JSON object/array (if JSON), or converted JSON from XML
 */
export function parseJsonXmlOrString(input: string): any {
  if (!input || typeof input !== 'string') {
    return input;
  }

  // Trim whitespace
  const trimmed = input.trim();

  if (trimmed === '') {
    return trimmed;
  }

  // Try to parse as JSON first
  if (isJsonString(trimmed)) {
    try {
      return JSON.parse(trimmed);
    } catch (e) {
      // If parsing fails, return as string
      return input;
    }
  }

  // Check if it's XML
  if (isXmlString(trimmed)) {
    try {
      parseString(trimmed, { 
        explicitArray: true, 
        mergeAttrs: false,
        async: false 
      }, (err, parsed) => {
        if (err) {
          throw err;
        }
      });
      
      // xml2js might be async, so we use the sync version
      // For TypeScript compatibility, we'll use a different approach
      return parseXmlSync(trimmed);
    } catch (e) {
      // If XML parsing fails, return as string
      return input;
    }
  }

  // Return as plain string
  return input;
}

/**
 * Checks if a string is valid JSON
 */
function isJsonString(str: string): boolean {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Checks if a string looks like XML
 */
function isXmlString(str: string): boolean {
  const trimmed = str.trim();
  return trimmed.startsWith('<') && trimmed.endsWith('>');
}

/**
 * Parses XML synchronously (workaround for xml2js async behavior)
 */
function parseXmlSync(xmlString: string): any {
  try {
    let result: any = null;
    let parseError: any = null;
    
    parseString(xmlString, {
      explicitArray: true,
      mergeAttrs: false,
      ignoreAttrs: false,
      trim: true
    }, (err, parsed) => {
      if (err) {
        parseError = err;
        return;
      }
    });

    if (parseError) {
      throw parseError;
    }

    return result || xmlString;
  } catch (e) {
    return xmlString;
  }
}

/**
 * Async version that returns a Promise
 * @param input - The input string that can be JSON, XML, or plain text
 * @returns Promise<any> - resolves with the parsed result
 */
export async function parseJsonXmlOrStringAsync(input: string): Promise<any> {
  if (!input || typeof input !== 'string') {
    return input;
  }

  const trimmed = input.trim();

  if (trimmed === '') {
    return trimmed;
  }

  // Try to parse as JSON first
  if (isJsonString(trimmed)) {
    try {
      return JSON.parse(trimmed);
    } catch (e) {
      return input;
    }
  }

  // Check if it's XML and parse it
  if (isXmlString(trimmed)) {
    try {
      return await new Promise((resolve, reject) => {
        parseString(trimmed, {
          explicitArray: true,
          mergeAttrs: false,
          ignoreAttrs: false,
          trim: true
        }, (err, parsed) => {
          if (err) {
            resolve(input); // Return original string if parsing fails
          } else {
            resolve(parsed);
          }
        });
      });
    } catch (e) {
      return input;
    }
  }

  return input;
}


/**
 * Flattens a nested object into underscore-delimited keys.
 * - Keeps top-level scalar fields as-is
 * - For arrays, the first element's keys have no numeric suffix; subsequent elements get _2, _3, ...
 * - Numeric-like strings are converted to numbers
 */
export function flattenFinalJson(input: any): Record<string, any> {
  const result: Record<string, any> = {};

  function isPlainObject(value: any): boolean {
    return Object.prototype.toString.call(value) === '[object Object]';
  }

  function coerceValue(value: any): any {
    if (typeof value !== 'string') {
      return value;
    }
    const trimmed = value.trim();
    if (/^-?\d+(?:\.\d+)?$/.test(trimmed)) {
      const asNumber = Number(trimmed);
      // Guard against numeric overflow converting to NaN or Infinity
      return Number.isFinite(asNumber) ? asNumber : value;
    }
    return value;
  }

  function setLeaf(pathParts: string[], value: any, arrayIndex: number | null): void {
    const baseKey = pathParts.filter(Boolean).join('_');
    const key = arrayIndex && arrayIndex > 0 ? `${baseKey}_${arrayIndex + 1}` : baseKey;
    result[key] = coerceValue(value);
  }

  /**
   * Attempts to parse a JSON string, returns parsed value or original if parsing fails
   */
  function tryParseJsonString(value: string): any {
    if (value.trim() === '') {
      return value;
    }
    if (!isJsonString(value)) {
      return value;
    }
    try {
      return JSON.parse(value);
    } catch (e) {
      return value;
    }
  }

  /**
   * Processes an object property value that might be a JSON string
   */
  function processObjectProperty(value: any, pathParts: string[], arrayIndex: number | null): void {
    const parsedValue = typeof value === 'string' ? tryParseJsonString(value) : value;
    
    if (Array.isArray(parsedValue) || isPlainObject(parsedValue)) {
      flatten(parsedValue, pathParts, arrayIndex);
    } else {
      setLeaf(pathParts, parsedValue, arrayIndex);
    }
  }

  function flatten(value: any, pathParts: string[] = [], arrayIndex: number | null = null): void {
    // Try parsing JSON string if value is a string
    const parsedValue = typeof value === 'string' ? tryParseJsonString(value) : value;
    
    if (parsedValue !== value && (isPlainObject(parsedValue) || Array.isArray(parsedValue))) {
      flatten(parsedValue, pathParts, arrayIndex);
      return;
    }

    if (Array.isArray(parsedValue)) {
      for (let i = 0; i < parsedValue.length; i += 1) {
        flatten(parsedValue[i], pathParts, i);
      }
      return;
    }

    if (isPlainObject(parsedValue)) {
      for (const key of Object.keys(parsedValue)) {
        const next = parsedValue[key];
        const nextPath = [...pathParts, key];
        processObjectProperty(next, nextPath, arrayIndex);
      }
      return;
    }

    // Primitive leaf at current path
    setLeaf(pathParts, parsedValue, arrayIndex);
  }

  if (!isPlainObject(input)) {
    result['value'] = coerceValue(input);
    return result;
  }

  // Process each top-level property
  for (const key of Object.keys(input)) {
    const value = input[key];
    const parsedValue = typeof value === 'string' ? tryParseJsonString(value) : value;
    
    if (isPlainObject(parsedValue) || Array.isArray(parsedValue)) {
      flatten(parsedValue, [key], null);
    } else {
      result[key] = coerceValue(parsedValue);
    }
  }

  return result;
}

/**
 * Converts a flat object into an array of { name, value } entries
 */
export function flattenedToNameValueArray(input: any): Array<{ name: string; value: any }> {
  if (!input || Object.prototype.toString.call(input) !== '[object Object]') {
    return [];
  }
  const entries: Array<{ name: string; value: any }> = [];
  for (const key of Object.keys(input)) {
    entries.push({ name: key, value: input[key] });
  }
  return entries;
}

/**
 * Pipeline: parse (string | xml | json) -> flatten -> name/value pairs
 * Supports arrays of objects - processes each object and combines results
 */
export async function parseFlattenToPairs(input: any): Promise<Array<{ name: string; value: any }>> {
  let parsed: any = input;

  if (typeof input === 'string') {
    parsed = await parseJsonXmlOrStringAsync(input);
  }

  // Handle arrays of objects
  if (Array.isArray(parsed)) {
    const allResults: Array<{ name: string; value: any }> = [];
    
    for (const item of parsed) {
      // Only process objects in the array
      if (item && Object.prototype.toString.call(item) === '[object Object]') {
        const flat = flattenFinalJson(item);
        const itemResults = flattenedToNameValueArray(flat);
        allResults.push(...itemResults);
      }
    }
    
    return allResults;
  }

  // Handle single object
  if (!parsed || Object.prototype.toString.call(parsed) !== '[object Object]') {
    return [];
  }

  const flat = flattenFinalJson(parsed);
  const final = flattenedToNameValueArray(flat);
  return final;
}